<template>
    <v-row class="pa-2">
        <v-col>
            <v-overlay opacity="0.9" :absolute="true" :value="showPortraitOverlay" class="text-center align-start" :class="{'overlay-landscape': showBtnAuth}">
                <v-icon style="font-size: 140px; padding-top: 76px;">screen_rotation</v-icon>
                <p class="text-h5 text-uppercase white--text mt-4">Please turn your device<br>into landscape mode</p>
            </v-overlay>

            <v-sheet class="vsheet-header">
                <v-toolbar flat>
                    <v-dialog v-model="dialogDate" max-width="900" content-class="booking-dialog">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-if="showBtnAuth" class="mr-4" dark color="red darken-2" @click="openBooking">
                                New Booking <v-icon right>insert_invitation</v-icon>
                            </v-btn>
                        </template>
                        <v-card>
                            <v-container class="pa-2">
                                <v-stepper v-model.lazy="step" vertical>
                                    <v-stepper-step
                                        :editable="editablePanel"
                                        step="1"
                                        :rules="[() => !errors.any('step1')]"
                                        :complete="step > 1"
                                        :color="step > 1 ? 'success' : 'primary'"
                                    >
                                        Venue
                                    </v-stepper-step>

                                    <v-stepper-content step="1">
                                        <v-form class="overwrite-inputs" data-vv-scope="step1" lazy-validation>
                                            <v-card class="mb-6 pa-4" color="grey lighten-4" height="200px">
                                                <p class="subtitle-2 font-weight-medium mb-6">Venue selected previously on calendar view:</p>
                                                <p class="subtitle-2 font-weight-bold mb-6"><i class="v-icon notranslate v-icon--left material-icons theme--light pb-2">location_city</i>{{VenueName}}</p>
                                            </v-card>
                                            <v-btn color="primary" @click.native="submitStep('step1')">Continue</v-btn>
                                        </v-form>
                                    </v-stepper-content>

                                    <v-stepper-step
                                        :editable="editablePanel"
                                        step="2"
                                        :rules="[() => !errors.any('step2')]"
                                        :complete="step > 2"
                                        :color="step > 2 ? 'success' : 'primary'"
                                    >
                                        Sport
                                    </v-stepper-step>

                                    <v-stepper-content step="2">
                                        <v-form class="overwrite-inputs" data-vv-scope="step2" lazy-validation>
                                            <v-card class="mb-6 pa-4" color="grey lighten-4" height="200px">
                                                <p class="subtitle-2 font-weight-medium mb-6">Which sport do you want to play?</p>
                                                <v-select
                                                    prepend-icon="sports_basketball"
                                                    :items="filteredSports"
                                                    v-model.lazy="sportSel"
                                                    return-object
                                                    single-line
                                                    item-text="sport_name"
                                                    item-value="sport_id"
                                                    label="Sports"
                                                    v-validate="'required'"
                                                    :error="errors.has('booking.sportSel')"
                                                    :error-messages="errors.collect('booking.sportSel')"
                                                    data-vv-name="booking.sportSel"
                                                ></v-select>
                                            </v-card>
                                            <v-btn color="primary" @click.native="submitStep('step2')">Continue</v-btn>
                                            <v-btn color="error" text @click="step = 1">Back</v-btn>
                                        </v-form>
                                    </v-stepper-content>

                                    <v-stepper-step
                                        :editable="editablePanel"
                                        step="3"
                                        :rules="[() => !errors.any('step3')]"
                                        :complete="step > 3"
                                        :color="step > 3 ? 'success' : 'primary'"
                                    >
                                        Type/Option
                                    </v-stepper-step>

                                    <v-stepper-content step="3">
                                        <v-form class="overwrite-inputs" data-vv-scope="step3" lazy-validation>
                                            <v-card class="mb-6 pa-4" color="grey lighten-4" height="200px">
                                                <v-select
                                                    prepend-icon="sports_basketball"
                                                    :items="filteredTypes"
                                                    v-model.lazy="typeSel"
                                                    return-object
                                                    single-line
                                                    item-text="type_name"
                                                    item-value="type_id"
                                                    label="Type"
                                                    hint="Type"
                                                    persistent-hint
                                                    v-validate="'required'"
                                                    :error="errors.has('booking.typeSel')"
                                                    :error-messages="errors.collect('booking.typeSel')"
                                                    data-vv-name="booking.typeSel"
                                                ></v-select>
                                                <v-select
                                                    prepend-icon="sports_basketball"
                                                    :items="filteredOptions"
                                                    v-model.lazy="optionSel"
                                                    return-object
                                                    single-line
                                                    item-text="option_name"
                                                    item-value="option_id"
                                                    label="Option"
                                                    hint="Option"
                                                    persistent-hint
                                                    clearable
                                                ></v-select>
                                            </v-card>
                                            <v-btn color="primary" @click.native="submitStep('step3')">Continue</v-btn>
                                            <v-btn color="error" text @click="step = 2">Back</v-btn>
                                        </v-form>
                                    </v-stepper-content>

                                    <v-stepper-step
                                        :editable="editablePanel"
                                        step="4"
                                        :rules="[() => !errors.any('step4')]"
                                        :complete="step > 4"
                                        :color="step > 4 ? 'success' : 'primary'"
                                    >
                                        Court
                                    </v-stepper-step>

                                    <v-stepper-content step="4">
                                        <v-form class="overwrite-inputs" data-vv-scope="step4" lazy-validation>
                                            <v-card class="mb-6 pa-4" color="grey lighten-4" height="200px">
                                                <p class="subtitle-2 font-weight-medium mb-6">Which court do you want to play on?</p>
                                                <v-select
                                                    prepend-icon="location_on"
                                                    :items="filteredCourts"
                                                    v-model.lazy="courtSel"
                                                    return-object
                                                    single-line
                                                    item-text="court_name"
                                                    item-value="court_id"
                                                    label="Courts"
                                                    v-validate="'required'"
                                                    :error="errors.has('booking.courtSel')"
                                                    :error-messages="errors.collect('booking.courtSel')"
                                                    data-vv-name="booking.courtSel"
                                                ></v-select>
                                            </v-card>
                                            <v-btn color="primary" @click.native="submitStep('step4')">Continue</v-btn>
                                            <v-btn color="error" text @click="step = 3">Back</v-btn>
                                        </v-form>
                                    </v-stepper-content>

                                    <v-stepper-step
                                        :editable="editablePanel"
                                        step="5"
                                        :rules="[() => !errors.any('step5')]"
                                        :complete="step > 5"
                                        :color="step > 5 ? 'success' : 'primary'"
                                    >
                                        Date/Time
                                    </v-stepper-step>

                                    <v-stepper-content step="5">
                                        <v-form class="overwrite-inputs" data-vv-scope="step5" lazy-validation>
                                            <v-card class="mb-6 pa-4" color="grey lighten-4">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <p class="subtitle-2 font-weight-medium mb-4">Booking Date</p>
                                                        <v-alert v-if="showAlertBookingWindow" dense elevation="2" text type="error" class="mb-6">
                                                            Booking can only be made between {{bookingWindowStartTime}} hours and {{bookingWindowEndTime}} days from now.
                                                        </v-alert>
                                                        <v-alert v-if="showBookAvailAlert" dense elevation="2" text type="error" class="mb-6">
                                                            Booking not available: no availability hours.
                                                        </v-alert>
                                                        <!-- <v-menu
                                                            ref="bookingDate"
                                                            v-model="bookingDate"
                                                            :close-on-content-click="false"
                                                            :return-value.sync="bookingDay"
                                                            transition="scale-transition"
                                                            offset-y
                                                            min-width="auto"
                                                        >
                                                            <template v-slot:activator="{ on, attrs }"> -->
                                                                <v-text-field
                                                                    v-model="bookingDayFormatted"
                                                                    label="Booking Date"
                                                                    prepend-icon="today"
                                                                    readonly
                                                                    disabled
                                                                    v-validate="'required|date_format:dd/MM/yyyy'"
                                                                    :error="errors.has('booking.bookingDay')"
                                                                    :error-messages="errors.collect('booking.bookingDay')"
                                                                    data-vv-name="booking.bookingDay"
                                                                ></v-text-field>
                                                            <!-- </template>
                                                            <v-date-picker v-model="bookingDay" no-title scrollable @change="$refs.bookingDate.save(bookingDay)" :min="bookingWindowStartDay" :max="bookingWindowEndDay">
                                                            </v-date-picker>
                                                        </v-menu> -->
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12" class="pb-0">
                                                        <p class="subtitle-2 font-weight-medium ma-0">Choose Start / End Time</p>
                                                    </v-col>
                                                    <v-col cols="12" sm="6">
                                                        <vue-timepicker :disabled="disabledTimePicker" drop-direction="up" v-model="timeStart" close-on-complete auto-scroll hour-label="Hours" hide-disabled-hours hide-disabled-minutes minute-label="Minutes" :minute-interval="15" placeholder="Start Time" :hour-range="[[minHours.substring(0,2), maxHoursBook.substring(0,2)]]" :minute-range="minuteRange" @change="changeHandler" style="padding: 2px 0 0 25px;">
                                                            <template v-slot:icon>
                                                                <v-icon left>schedule</v-icon>
                                                            </template>
                                                        </vue-timepicker>
                                                        <v-input
                                                            ref="timeStart"
                                                            v-model="timeStartString"
                                                            v-validate="'required|date_format:HH:mm'"
                                                            :error="errors.has('booking.timeStart')"
                                                            :error-messages="errors.collect('booking.timeStart')"
                                                            data-vv-name="booking.timeStart"
                                                        ></v-input>
                                                    </v-col>
                                                    <v-col cols="12" sm="6">
                                                        <vue-timepicker :disabled="disabledTimePicker" drop-direction="up" v-model="timeEnd" close-on-complete auto-scroll hour-label="Hours" hide-disabled-hours hide-disabled-minutes minute-label="Minutes" :minute-interval="15" placeholder="End Time" :hour-range="[[minHours.substring(0,2), maxHoursBook.substring(0,2)]]" :minute-range="minuteRange" @change="changeHandler" style="padding: 2px 0 0 25px;">
                                                            <template v-slot:icon>
                                                                <v-icon left>schedule</v-icon>
                                                            </template>
                                                        </vue-timepicker>
                                                        <v-input
                                                            ref="timeEnd"
                                                            v-model="timeEndString"
                                                            v-validate="'required|date_format:HH:mm|after:timeStart'"
                                                            :error="errors.has('booking.timeEnd')"
                                                            :error-messages="errors.collect('booking.timeEnd')"
                                                            data-vv-name="booking.timeEnd"
                                                        ></v-input>
                                                    </v-col>
                                                </v-row>
                                            </v-card>
                                            <v-btn color="primary" @click.native="submitStep('step5')" :disabled="disabledTimePicker">Continue</v-btn>
                                            <v-btn color="error" text @click="step = 4">Back</v-btn>
                                        </v-form>
                                    </v-stepper-content>

                                    <v-stepper-step
                                        :editable="editablePanel"
                                        step="6"
                                        :rules="[() => !errors.any('step6')]"
                                        :complete="step > 6"
                                        :color="step > 6 ? 'success' : 'primary'"
                                    >
                                        Review
                                    </v-stepper-step>

                                    <v-stepper-content step="6">
                                        <v-alert class="mb-4" outlined transition="scale-transition" type="error" v-if="showBtnAuth && !bookable && !checkRateReview(rateReview)">{{ getRateReview(rateReview) }}</v-alert>

                                        <v-card class="mb-6 pa-4" color="grey lighten-4">
                                            <div>
                                                <v-row class="justify-start pa-4">
                                                    <div class="mr-4" v-if="venueSel"><b>Venue:</b> {{ venueSel.venue_name }}</div>
                                                    <div class="mr-4" v-if="sportSel"><b>Sport:</b> {{ sportSel.sport_name }}</div>
                                                </v-row>
                                                <v-row class="justify-start pa-4">
                                                    <div class="mr-4" v-if="typeSel"><b>Type:</b> {{ typeSel.type_name }}</div>
                                                    <div class="mr-4" v-if="optionSel"><b>Option:</b> {{ optionSel.option_name }}</div>
                                                    <div class="mr-4" v-if="courtSel"><b>Court:</b> {{ courtSel.court_name }}</div>
                                                </v-row>
                                                <v-row class="justify-start pa-4">
                                                    <div class="mr-4" v-if="bookingDay"><b>Booking Date:</b> {{ bookingDayFormatted }}</div>
                                                    <div class="mr-4" v-if="timeStart && timeEnd"><b>Booking Time:</b> From {{ timeStartString }} TO {{ timeEndString }}</div>
                                                </v-row>
                                                <!-- <v-row v-if="checkRateReview(rateReview)" class="justify-start pa-0">
                                                    <div style="display: none !important;"><b>Rate:</b> {{ getRateReview(rateReview) }}</div>
                                                </v-row> -->
                                                <v-row class="justify-start pa-4" v-if="couponApplied">
                                                    <div><b>{{ appNameUrls.client }} Voucher:</b> {{ discountTotal | currency("$") }} <v-btn v-if="couponApplied" small dark color="red" @click="removeVoucher">remove</v-btn></div>
                                                </v-row>
                                                <v-row v-if="checkRateReview(rateReview)" class="justify-start pa-4">
                                                    <div><b>Total:</b> {{ grandTotal | currency("$") }}</div>
                                                </v-row>
                                            </div>
                                        </v-card>

                                        <v-btn v-if="!couponApplied" :disabled="!bookable" color="primary" @click="step = 7">Continue</v-btn>
                                        <v-btn v-if="couponApplied" color="primary" @click="step = 8">Continue</v-btn>
                                        <v-btn color="error" text @click="step = 5">Back</v-btn>
                                    </v-stepper-content>

                                    <!-- <v-stepper-step
                                        :editable="editablePanel"
                                        :rules="[() => !couponErrors.any()]"
                                        :complete="step > 7 || couponApplied"
                                        step="7"
                                        :color="(couponApplied || step > 7) ? 'success' : 'primary'"
                                    >
                                        Enter {{ appNameUrls.client }} Voucher
                                        <small>{{ getCouponMessage }}</small>
                                    </v-stepper-step>

                                    <v-stepper-content step="7">
                                        <v-card outlined color="grey lighten-5" class="mb-5 elevetation-6">
                                            <v-expansion-panels :value="0">
                                                <v-expansion-panel class="secondary white--text">
                                                    <v-expansion-panel-header>{{ appNameUrls.client }} Voucher</v-expansion-panel-header>
                                                    <v-expansion-panel-content>
                                                        <v-card>
                                                            <v-card-text>
                                                                <coupon :grand-total="subTotal" :akv="false" @submitted-voucher="submittedVoucher"></coupon>
                                                            </v-card-text>
                                                        </v-card>
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </v-card>
                                        <v-btn color="primary" @click.native="submitStep('step7')">Continue</v-btn>
                                        <v-btn color="error" text @click="step = 6">Back</v-btn>
                                    </v-stepper-content> -->

                                    <v-stepper-step
                                        :editable="editablePanel"
                                        :rules="[() => !paymentErrors.any()]"
                                        :complete="step > 7"
                                        step="7"
                                    >
                                        Make Online Payment {{ grandTotal | currency }}
                                        <small
                                            v-if="paymentErrors.any() && !paymentErrors.has('error_response')"
                                            class="caption font-weight-bold secondary--text"
                                            v-html="getPaymentMessage"
                                        ></small>
                                        <small
                                            v-if="paymentErrors.any() && paymentErrors.has('error_response')"
                                            class="caption font-weight-bold secondary--text"
                                            v-html="paymentErrors.get('error_response')"
                                        ></small>
                                    </v-stepper-step>

                                    <v-stepper-content step="7">
                                        <donation v-if="showCpDonation"></donation>

                                        <v-card color="grey lighten-5" class="mb-5 elevetation-6">
                                            <v-card-text>
                                                <component :is="ftGateway" v-if="grandTotal > 0" @submitted-pay="submitPay"></component>
                                                <v-alert dense outlined v-if="grandTotal <= 0" type="info">No Payment required. Click button below to complete process.</v-alert>
                                            </v-card-text>
                                        </v-card>
                                        <v-alert outlined transition="scale-transition" type="error" v-if="showBtnAuth && !bookable">{{ getRateReview(rateReview) }}</v-alert>
                                        <v-btn v-if="showBtnAuth && bookable" :loading="payLoading" :disabled="!validPaymentForm" color="primary" @click="paybooking" class="mr-2 text-left">
                                            <v-icon left>local_atm</v-icon>{{ payHtml }}: {{ grandTotal | currency }}
                                            </v-btn>
                                        <v-btn v-if="grandTotal > 0" @click="clearPay" color="warning" :disabled="payLoading" class="text-left">clear
                                            <v-icon right>clear</v-icon>
                                        </v-btn>
                                        <v-btn color="error" text @click="step = 6">Back</v-btn>
                                    </v-stepper-content>
                                </v-stepper>
                            </v-container>
                        </v-card>
                    </v-dialog>

                    <v-btn v-if="!showBtnAuth" class="mr-4" dark color="red darken-2" :to="{ name: 'bookings', query: { venue: venueQueryId }}">Book<v-icon right>launch</v-icon></v-btn>

                    <v-menu
                        ref="focusDate"
                        v-model="focusDate"
                        :close-on-content-click="true"
                        :return-value.sync="focus"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="focusDateFormatted"
                                label=""
                                readonly
                                append-icon="keyboard_arrow_down"
                                v-bind="attrs"
                                v-on="on"
                                class="mt-4 focus-date-text"
                                @click:append="focusDate = true;"
                            ></v-text-field>
                        </template>
                        <v-date-picker v-model="focus" no-title scrollable @change="$refs.focusDate.save(focus);" :min="bookingWindowStartDay">
                            <v-spacer></v-spacer>
                            <v-btn text color="primary" @click="focus=today;$refs.focusDate.save(focus);showAlertBookingWindow=false;">Today</v-btn>
                        </v-date-picker>
                    </v-menu>

                    <v-spacer></v-spacer>
                    <v-select
                        dense
                        hide-details
                        :items="venues"
                        v-model.lazy="venueSel"
                        return-object
                        single-line
                        item-text="venue_name"
                        item-value="venue_id"
                        label="Venues"
                        @change="updateAll"
                        class="venueHeader"
                    ></v-select>
                    <v-btn v-if="showBtnAuth" class="ml-4" dark color="info" :to="{ name: 'mybookings'}">My History<v-icon right>event</v-icon></v-btn>
                </v-toolbar>
            </v-sheet>

            <v-overlay :absolute="true" :value="overlay" z-index="99" opacity="0.56">
                <v-card>
                    <v-card-text v-html="overlayText"></v-card-text>
                    <v-card-actions>
                        <v-btn medium block dark :color="overlayColor" @click="overlay = false">Close <v-icon medium right>highlight_off</v-icon> </v-btn>
                    </v-card-actions>
                </v-card>
            </v-overlay>

            <v-sheet height="685">
                <v-calendar
                    ref="calendar"
                    v-model="focus"
                    color="primary"
                    :events="events"
                    :event-color="getEventColor"
                    :event-ripple="false"
                    :event-margin-bottom="1"
                    event-overlap-mode="column"
                    event-overlap-threshold="0"
                    interval-height="30"
                    :first-time="firstTime"
                    interval-minutes="30"
                    interval-count="34"
                    :now="today"
                    :type="type"
                    show-week
                    category-show-all
                    category-days="1"
                    :categories="courts"
                    category-text="court_name"
                    @click:event="showEvent"
                    @click:more="viewDay"
                    @click:date="viewDay"
                    @change="updateRange"
                >
                    <template v-slot:event="{ event, timed }">
                        <div class="v-event-draggable d-flex" ref="event" :class="event.type.type_slug">
                            <div class=""><v-icon small left color="grey darken-4">event_available</v-icon></div>
                            <div>{{ event.start | moment("HH:mm") }} - {{ event.end | moment("HH:mm") }}</div>
                        </div>
                    </template>
                    <template v-slot:interval="{ weekday, time, category }">
                        <div :class="{ noBook: checkAvailTime(weekday, time, category) }"></div>
                    </template>
                </v-calendar>
                <v-menu v-model="selectedOpen" :close-on-content-click="false" :activator="selectedElement" offset-y>
                    <v-card color="grey lighten-4" min-width="300px" flat>
                        <v-toolbar color="#9e9e9e" dark>
                            <v-toolbar-title><v-icon small left color="grey darken-4">event_available</v-icon> Court Booking</v-toolbar-title>
                        </v-toolbar>
                        <v-card-text>
                            <div>
                                <v-row class="justify-start pa-3">
                                    <div v-if="selectedEvent.venue" class="mr-4"><b>Venue:</b> {{ selectedEvent.venue.venue_name }}</div>
                                </v-row>
                                <v-row class="justify-start pa-3" v-if="selectedEvent.type || selectedEvent.option">
                                    <div v-if="selectedEvent.type" class="mr-4"><b>Type:</b> {{ selectedEvent.type.type_name }}</div>
                                </v-row>
                                <v-row class="justify-start pa-3">
                                    <div class="mr-4" v-if="selectedEvent.court"><b>Court:</b> {{ selectedEvent.court.court_name }}</div>
                                    <div class="mr-4" v-if="selectedEvent.start"><b>From:</b> {{ selectedEvent.start | moment("DD/MM/YYYY HH:mm") }}</div>
                                    <div v-if="selectedEvent.end"><b>To:</b> {{ selectedEvent.end | moment("DD/MM/YYYY HH:mm") }}</div>
                                </v-row>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-menu>
            </v-sheet>
        </v-col>
    </v-row>
</template>

<script>
import store from "./_store";
import moment from "moment";

import { EventBus } from "@shared/EventBus.js";
import Eway from "@components/gateways/Eway.vue";
import MerchantWarrior from "@components/gateways/MerchantWarrior.vue";
import Coupon from "@components/coupons/Coupon.vue";
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';
import Donation from "@components/donation/Donation.vue";

export default {
    name: "BookingModule",
    components: {
        eway: Eway,
        merchantWarrior: MerchantWarrior,
        coupon: Coupon,
        VueTimepicker: VueTimepicker,
        donation: Donation,
    },
    $_veeValidate: {
        validator: "new",
    },
    data() {
        return {
            today: new Date()
                .toLocaleString("en-AU", { timeZone: this.lmpTz, hour12: false })
                .split(", ")[0]
                .split("/")
                .reverse()
                .join("-"),
            focus: new Date()
                .toLocaleString("en-AU", { timeZone: this.lmpTz, hour12: false })
                .split(", ")[0]
                .split("/")
                .reverse()
                .join("-"),
            type: "category",
            typeToLabel: {
                month: "Month",
                week: "Week",
                category: "Day",
            },
            step: 1,
            editablePanel: false,
            prevBtnDisabled: true,
            checkRuleStep: 6,
            checkAvailStep: 5,
            venueQueryId: null,
            venueSel: null,
            sportSel: null,
            optionSel: null,
            courtSel: null,
            typeSel: null,
            bookingDay: new Date()
                .toLocaleString("en-AU", { timeZone: this.lmpTz, hour12: false })
                .split(", ")[0]
                .split("/")
                .reverse()
                .join("-"),
            bookable: true,
            bookingDate: false,
            focusDate: false,
            menuDate: false,
            showAlertBookingWindow: false,
            timeStart: null,
            timeEnd: null,
            bookingStart: false,
            menuStart: false,
            bookingEnd: false,
            menuEnd: false,
            name: null,
            start: null,
            end: null,
            categ: null,
            selectedEvent: {},
            selectedElement: null,
            selectedOpen: false,
            dialogDate: false,
            events: [],
            courts: [],
            venues: [],
            sports: [],
            options: [],
            types: [],
            createEvent: null,
            rateReview: '0.0',

            bookingWindowStartDay: moment().format('YYYY-MM-DD'),
            bookingWindowEndDay: moment().add(28, 'days').format('YYYY-MM-DD'),
            firstTime: '06:00',
            firstTimeBook: '09:00',
            maxHoursBook: '21:00',
            maxHours: '23:00',
            bookingWindowStartTime: 0,
            bookingWindowEndTime: 28,
            minuteRange: [0,15,30,45],
            disabledTimePicker: true,
            showBookAvailAlert: false,
            availBookArr: [],
            // availBookStart: null,
            // availBookEnd: null,

            overlay: false,
            timeout: 500,
            overlayText: "",
            overlayColor: "cyan darken-2",
            ftGateway: gtwPw,
            showCpDonation: showDonation,
            validPaymentForm: true,

            showPortraitOverlay: false,
            baseUrl: window.LMP.baseUrl,

            dictionary: {
                messages: {
                    required: () => "This field is required",
                    email: () => "This field must be a valid email",
                    date_format: () => "This field must be a valid date/time",
                    numeric: () => "This field is number only",
                    min: (f, v) => "This field must be at least " + v + " characters",
                    confirmed: (f, v) => "This field confirmation does not match " + v,
                    regex: () => "This field must contain at least 1 uppercase letter, 1 lowercase letter and 1 numeric character",
                    before: () => "Start Time must be before End Time",
                    after: () => "End Time must be after Start Time",
                },
            },
        };
    },
    mounted() {
        this.$refs.calendar.checkChange();

        this.$validator.localize("en", this.dictionary);

        this.$refs.calendar.scrollToTime(
            new Date()
                .toLocaleString("en-AU", { timeZone: this.lmpTz, hour12: false })
                .split(", ")[1]
                .split(" ")[0]
        );

        window.addEventListener("orientationchange",this.handleOrientationChange);
        this.handleOrientationChange();

        this.getVenues();
    },
    watch: {
        focus(n, o) {
            this.bookingDay = n;
        },
        events(n, o){
            this.$nextTick(function(){
                if (document.querySelector('.v-event-draggable.full-space') !== null) {
                    let fullSpace = document.querySelectorAll(".v-event-draggable.full-space");
                    let i;
                    for (i = 0; i < fullSpace.length; ++i) {
                        fullSpace[i].parentElement.style.width = '100%';
                        fullSpace[i].parentElement.classList.add("full-court");
                        if(fullSpace[i].parentElement.previousSibling){
                            // fullSpace[i].parentElement.style.left = fullSpace[i].parentElement.previousSibling.style.width
                        };
                    }
                }
                if (document.querySelector('.v-event-draggable.full-court') !== null) {
                    let fullSpace = document.querySelectorAll(".v-event-draggable.full-court");
                    let i;
                    for (i = 0; i < fullSpace.length; ++i) {
                        fullSpace[i].parentElement.style.width = '100%';
                        fullSpace[i].parentElement.classList.add("full-court");
                        if(fullSpace[i].parentElement.previousSibling){
                            // fullSpace[i].parentElement.style.left = fullSpace[i].parentElement.previousSibling.style.width
                        };
                    }
                }
                if (document.querySelector('.v-event-draggable.two-third-court') !== null) {
                    let twothirdSpace = document.querySelectorAll(".v-event-draggable.two-third-court");
                    let i;
                    for (i = 0; i < twothirdSpace.length; ++i) {
                        twothirdSpace[i].parentElement.style.width = '66.66%';
                        twothirdSpace[i].parentElement.classList.add("twothird-court");
                        if(twothirdSpace[i].parentElement.style.left == '50%'){
                            twothirdSpace[i].parentElement.style.left = '33.33%';
                            if(twothirdSpace[i].parentElement.previousSibling){
                                // twothirdSpace[i].parentElement.style.left = twothirdSpace[i].parentElement.previousSibling.style.width;
                            }
                        }
                    }
                }
                if (document.querySelector('.v-event-draggable.third-court') !== null) {
                    let thirdSpace = document.querySelectorAll(".v-event-draggable.third-court");
                    let i;
                    for (i = 0; i < thirdSpace.length; ++i) {
                        thirdSpace[i].parentElement.style.width = '33.33%';
                        thirdSpace[i].parentElement.classList.add("third-court");
                        if(thirdSpace[i].parentElement.style.left == '50%'){
                            thirdSpace[i].parentElement.style.left = '33.33%';
                            if(thirdSpace[i].parentElement.previousSibling){
                                // thirdSpace[i].parentElement.style.left = thirdSpace[i].parentElement.previousSibling.style.width;
                            }
                        }
                    }
                }
                if (document.querySelector('.v-event-draggable.half-court') !== null) {
                    let halfSpace = document.querySelectorAll(".v-event-draggable.half-court");
                    let i;
                    for (i = 0; i < halfSpace.length; ++i) {
                        halfSpace[i].parentElement.style.width = '50%';
                        halfSpace[i].parentElement.classList.add("half-court");
                        if(halfSpace[i].parentElement.previousSibling){
                            // halfSpace[i].parentElement.style.left = halfSpace[i].parentElement.previousSibling.style.width
                        };
                    }
                }
            });
        },
        minHours(n, o){
            if(this.bookingDay == moment().format('YYYY-MM-DD')){
                if(parseInt(moment().add(this.bookingWindowStartTime, 'hours').format('HH:mm').replace(':', '')) > parseInt(this.maxHoursBook.replace(':', ''))){
                    this.disabledTimePicker = true;
                    // this.showBookAvailAlert = true;
                    this.showAlertBookingWindow = true;
                }
            }
        }
    },
    computed: {
        donationTotal() {
            return this.$store.getters["donation/donationTotal"];
        },
        VenueName(){
            if(this.venueSel){
                return this.venueSel.venue_name;
            }

            return '';
        },
        timeStartString(){
            if(this.timeStart){
                if(this.timeStart.HH && this.timeStart.mm){
                    return this.timeStart.HH +':' + this.timeStart.mm;
                }
            }

            return '';
        },
        timeEndString(){
            if(this.timeEnd){
                return this.timeEnd.HH +':' + this.timeEnd.mm;
            }

            return '';
        },
        minHours(){
            if(this.bookingDay != moment().format('YYYY-MM-DD')){
                return this.firstTimeBook;
            } else {
                if(parseInt(moment().add(this.bookingWindowStartTime, 'hours').format('HH:mm').replace(':', '')) > parseInt(this.maxHoursBook.replace(':', ''))){
                    return this.maxHoursBook;
                }
            }

           return moment().add(this.bookingWindowStartTime, 'hours').format('HH:mm');
        },
        bookingDayFormatted(){
            if(!this.bookingDay){
                return '';
            }

            return moment(this.bookingDay).format('DD/MM/YYYY');
        },
        discountTotal() {
            return this.$store.getters["coupon/discountTotal"];
        },
        couponApplied() {
            return this.$store.getters["coupon/couponApplied"];
        },
        couponErrors() {
            return this.$store.getters["coupon/couponErrors"];
        },
        couponLoading() {
            return this.$store.getters["coupon/couponStatus"] == "loading";
        },
        getCouponMessage() {
            return this.$store.getters["coupon/couponMessage"];
        },
        paymentErrors() {
            return this.$store.getters["booking/paymentErrors"];
        },
        getPaymentMessage() {
            if (this.paymentErrors.any()) {
                if (this.paymentErrors.has("error_auth")) {
                    return this.paymentErrors.get("error_auth");
                }

                if (this.paymentErrors.has("error_avail")) {
                    return this.paymentErrors.get("error_avail");
                }

                if (this.paymentErrors.has("error_collision")) {
                    return this.paymentErrors.get("error_collision");
                }

                if (this.paymentErrors.has("error_rule")) {
                    return this.paymentErrors.get("error_rule");
                }

                if (this.paymentErrors.has("error_rate")) {
                    return this.paymentErrors.get("error_rate");
                }

                if (this.paymentErrors.has("error_response")) {
                    return this.paymentErrors.get("error_response");
                }

                if (this.paymentErrors.has("error_validation")) {
                    return "Booking Data Invalid";
                }

                if (this.paymentErrors.has("error_payment")) {
                    return this.paymentErrors.get("error_payment");
                }

                return "Payment Invalid";
            }

            return "";
        },
        payLoading() {
            return this.$store.getters["booking/paymentStatus"] == "loading";
        },
        payHtml() {
            if (this.grandTotal > 0) {
                return "Pay & Book";
            }

            return "Complete Booking";
        },
        bookingCart() {
            return this.$store.getters["booking/bookingCart"];
        },

        subTotal() {
            return this.rateReview;
        },
        taxTotal() {
            return this.subTotal / (taxRate + 1);
        },
        grandTotal() {
            return this.subTotal - this.discountTotal + this.donationTotal;
        },
        focusDateFormatted () {
            return moment(this.focus).format('dddd Do MMMM YYYY');
        },
        title() {
            const { start, end } = this;
            if (!start || !end) {
                return "";
            }

            const weekDays = {
                0: 'Sun',
                1: 'Mon',
                2: 'Tue',
                3: 'Wed',
                4: 'Thu',
                5: 'Fri',
                6: 'Sat'
            };

            const dayDate = weekDays[start.weekday];
            const startMonth = this.monthFormatter(start);
            const endMonth = this.monthFormatter(end);
            const suffixMonth = startMonth === endMonth ? "" : endMonth;
            const startYear = start.year;
            const endYear = end.year;
            const suffixYear = startYear === endYear ? endYear : endYear;
            const startDay = start.day + this.nth(start.day);
            const endDay = end.day + this.nth(end.day);

            switch (this.type) {
                case "month":
                    return `${startMonth} ${startYear}`;
                case "week":
                    if(startMonth === endMonth){
                        return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
                    }

                    return `${startDay} ${startMonth} ${startYear} - ${endDay} ${suffixMonth} ${suffixYear}`;
                case "category":
                    return `${dayDate} ${startDay} ${startMonth} ${startYear}`;
            }

            return "";
        },
        filteredSports() {
            let sports = this.sports;

            if (_.isEmpty(this.venueSel)) {
                return sports;
            }

            let _self = this;

            return _.filter(sports, function(s) {
                return _.includes(s.venue_id, _self.venueSel.venue_id);
            });
        },
        filteredTypes() {
            let types = this.types;

            if (_.isEmpty(this.venueSel)) {
                return types;
            }

            let _self = this;

            let conditions = [];

            if (_self.venueSel) {
                conditions.push(function(item) {
                    return item.venue_id == _self.venueSel.venue_id;
                });
            }

            if (_self.sportSel) {
                conditions.push(function(item) {
                    return _.includes(item.sport_id, _self.sportSel.sport_id);
                });
            }

            return _.filter(types, function(type) {
                return _.every(conditions, function(condition) {
                    return condition(type);
                });
            });
        },
        filteredOptions() {
            let options = this.options;

            if (_.isEmpty(this.venueSel)) {
                return options;
            }

            let _self = this;

            let conditions = [];

            if (_self.venueSel) {
                conditions.push(function(item) {
                    return item.venue_id == _self.venueSel.venue_id;
                });
            }

            if (_self.sportSel) {
                conditions.push(function(item) {
                    return _.includes(item.sport_id, _self.sportSel.sport_id);
                });
            }

            return _.filter(options, function(option) {
                return _.every(conditions, function(condition) {
                    return condition(option);
                });
            });
        },
        filteredCourts() {
            let courts = this.courts;

            if (_.isEmpty(this.venueSel)) {
                return courts;
            }

            let _self = this;

            let conditions = [];

            if (_self.venueSel) {
                conditions.push(function(item) {
                    return item.venue_id == _self.venueSel.venue_id;
                });
            }

            if (_self.sportSel) {
                conditions.push(function(item) {
                    return _.includes(item.sport_id, _self.sportSel.sport_id);
                });
            }

            if (_self.typeSel) {
                conditions.push(function(item) {
                    // return item.type_id == _self.typeSel.type_id;
                    return _.includes(item.type_id, _self.typeSel.type_id);
                });
            }

            if (_self.optionSel) {
                conditions.push(function(item) {
                    return _.includes(item.option_id, _self.optionSel.option_id);
                });
            }

            return _.filter(courts, function(court) {
                return _.every(conditions, function(condition) {
                    return condition(court);
                });
            });
        },
        monthFormatter() {
            return this.$refs.calendar.getFormatter({
                timeZone: this.lmpTz,
                month: "long",
            });
        },
        showBtnAuth() {
            return this.$store.getters["auth/isAuthenticated"];
        },
    },
    methods: {
        checkAvailDay(){
            axios.post("/api/booking/events/checkavailday", {
                day: this.focus
            }).then(response => {
                // this.availBookStart = moment(this.focus + " " + response.data.data.feedback[1].availability_from).format('HH:mm');
                // this.availBookEnd = moment(this.focus + " " + response.data.data.feedback[1].availability_to).format('HH:mm');
                this.availBookArr = response.data.data.feedback;
                // this.$forceUpdate();
            });
        },
        checkAvailTime(weekday, time, category){
            if(category == undefined){ //weekly view
                return false;
            }

            if(this.availBookArr[0][category.court_id]){
                if(this.availBookArr[0][category.court_id][0].availability_from > time){
                    return true;
                }

                if(this.availBookArr[0][category.court_id][0].availability_to <= time){
                    return true;
                }
            }

            if(!this.availBookArr[0][category.court_id]){
                return true;
            }

            return false;
        },
        handleOrientationChange(){
            let orientation = window.screen.orientation;
            // console.log(orientation);

            if (orientation !== undefined) {
                // let orientationSafari = window.matchMedia("(orientation: portrait)");
                // console.log(orientationSafari);

                // if(orientationSafari.matches) {
                //     this.showPortraitOverlay = true;
                // } else {
                //     this.showPortraitOverlay = false;
                // }
                if (orientation.type === "portrait-primary") {
                    this.showPortraitOverlay = true;
                } else if (orientation.type === "landscape-primary") {
                    this.showPortraitOverlay = false;
                }
            }
        },
        changeHandler(eventData){
            this.minuteRange = [0, 15, 30, 45];

            if(eventData.data.HH == this.minHours.substring(0,2)){
                switch (this.minHours.substring(3,5)) {
                    case '15':
                        this.minuteRange = [15, 30, 45];
                        break;
                    case '30':
                        this.minuteRange = [30, 45];
                        break;
                    case '45':
                        this.minuteRange = [45];
                        break;
                    default:
                        this.minuteRange = [0, 15, 30, 45];
                        break;
                }
            }

            if(eventData.data.HH == this.maxHoursBook.substring(0,2)){
                switch (this.maxHoursBook.substring(3,5)) {
                    case '15':
                        this.minuteRange = [0, 15];
                        break;
                    case '30':
                        this.minuteRange = [0, 15, 30];
                        break;
                    case '45':
                        this.minuteRange = [0, 15, 30, 45];
                        break;
                    default:
                        this.minuteRange = [0];
                        break;
                }
            }
        },
        openBooking(){
            this.step = 1;
            this.updateAll();

            this.bookingDay = this.focus;
            let focusCalDate = moment(this.focus, 'YYYY-MM-DD');
            let endBookingCalDate = moment(this.bookingWindowEndDay, 'YYYY-MM-DD');

            // console.log(this.bookingWindowEndDay, endBookingCalDate);

            this.showAlertBookingWindow = false;
            if (focusCalDate.isAfter(endBookingCalDate)) {
                this.showAlertBookingWindow = true;
                this.bookingDay = null;
            }

            this.$validator.reset();
            this.dialogDate = true;
        },
        allowedStep(m) {
            return m % 15 === 0;
        },
        submittedVoucher() {
            this.step = 6;
        },
        removeVoucher() {
            this.$store.dispatch("coupon/removeVoucher");
        },
        clearVoucher() {
            this.$store.dispatch("coupon/clearCouponErrors");
        },
        clearPay() {
            EventBus.$emit("clear-pay");
            this.$store.dispatch("booking/clearPaymentErrors");
        },
        paybooking: function() {
            if (this.grandTotal > 0) {
                EventBus.$emit("submit-pay");
            } else {
                this.submitPay();
            }
        },
        submitPay: function(payload) {
            this.validPaymentForm = false;

            const cardPayload = payload;
            const couponCart = this.$store.getters["coupon/couponCart"];
            const donationCart = this.$store.getters["donation/donationCart"];

            this.$store
                .dispatch("booking/payBooking", {
                    cardPayload: cardPayload,
                    couponCart: couponCart,
                    donationCart: donationCart,
                })
                .then(order_id => {
                    this.dialogDate = false;

                    this.overlayText = "Payment was successful.";
                    this.overlay = true;
                    this.overlayColor = "primary";
                    this.membReq = true;

                    this.clearVoucher();

                    setTimeout(() => {
                        // this.$store
                        //     .dispatch("order/orderRequest")
                        //     .then(() => {
                                this.$router.push({
                                    name: "orderDetail",
                                    params: { order_id: order_id },
                                });
                            // })
                            // .catch();
                    }, this.timeout / 4);
                })
                .catch(err => {
                    this.validPaymentForm = true;
                    // console.log(err);

                    setTimeout(() => {
                        EventBus.$emit("clear-pay");
                    }, 500);
                });
        },
        submitStep(scope) {
            this.$validator.validateAll(scope).then(result => {
                if (result) {
                    if (this.step == this.checkAvailStep - 1) {
                        axios
                            .post("/api/booking/events/checkavail", {
                                day: this.focus,
                                space: this.courtSel.court_id
                            })
                            .then(response => {
                                this.disabledTimePicker = false;
                                this.firstTimeBook = moment(this.focus + " " + response.data.data.feedback.availability_from).format('HH:mm');
                                this.maxHoursBook = moment(this.focus + " " + response.data.data.feedback.availability_to).format('HH:mm');
                                this.step++;
                                this.showBookAvailAlert = false;
                                // this.showAlertBookingWindow = false;
                                this.timeStart = null;
                                this.timeEnd = null;
                            }).catch(err => {
                                this.showBookAvailAlert = true;
                                this.disabledTimePicker = true;
                                // this.showAlertBookingWindow = false;
                                this.step++;
                                // console.log(err);
                            });
                    }else if (this.step == this.checkRuleStep - 1) {
                        axios
                            .post("/api/booking/events/checkrules", {
                                day: this.bookingDay,
                                start: this.timeStartString,
                                end: this.timeEndString,
                                space: this.courtSel.court_id,
                                type: this.typeSel ? this.typeSel.type_id : "",
                                option: this.optionSel ? this.optionSel.option_id : "",
                                sport: this.sportSel ? this.sportSel.sport_id : "",
                            })
                            .then(response => {
                                this.rateReview = response.data.data.feedback;
                                this.getRateReview(this.rateReview)
                                // this.editablePanel = true;
                                this.step++;
                            });
                    } else {
                        this.step++;
                    }
                }
            });
        },
        updateAll() {
            this.getCourts(this.venueSel.venue_id);
            this.getTypes(this.venueSel.venue_id);
            this.getOptions(this.venueSel.venue_id);
            this.getSports(this.venueSel.venue_id);
            this.getEvents(this.start.date, this.venueSel.venue_id);
            this.step = 1;
            this.sportSel =  null;
            this.optionSel = null;
            this.courtSel = null;
            this.typeSel =  null;
            this.bookingDay =  null;
            this.timeStart =  null;
            this.timeEnd =  null;
        },
        getEvents(start_date, venue) {
            axios.get("/api/booking/events/venue/" + venue + "/" + this.type + "/" + start_date).then(response => {
                this.events = response.data.data;
            });
        },
        getCourts(venue) {
            axios.get("/api/booking/courts/venue/" + venue).then(response => {
                this.courts = response.data.data;
            });
        },
        getVenues() {
            axios.get("/api/booking/venues").then(response => {
                this.venues = response.data.data;

                if (!_.isNaN(parseInt(this.$route.query.venue))) {
                    this.venueQueryId = parseInt(this.$route.query.venue);
                    let venueQuery = this.venues.filter(venue => {
                        return venue.venue_id == this.venueQueryId
                    });

                    if(venueQuery.length > 0){
                        this.venueSel = venueQuery[0];
                    } else {
                        this.venueSel = this.venues[0];
                    }
                }else{
                    this.venueSel = this.venues[0];
                }

                this.getCourts(this.venueSel.venue_id);
                this.getTypes(this.venueSel.venue_id);
                this.getOptions(this.venueSel.venue_id);
                this.getSports(this.venueSel.venue_id);
                this.getEvents(this.start.date, this.venueSel.venue_id);
            });
        },
        getSports(venue) {
            axios.get("/api/booking/sports/venue/" + venue).then(response => {
                this.sports = response.data.data;
            });
        },
        getOptions(venue) {
            axios.get("/api/booking/options/venue/" + venue).then(response => {
                this.options = response.data.data;
            });
        },
        getTypes(venue) {
            axios.get("/api/booking/types/venue/" + venue).then(response => {
                this.types = response.data.data;
            });
        },
        setDialogDate({ date }) {
            this.dialogDate = true;
            this.focus = date;
        },
        viewDay({ date }) {
            this.focus = date;
            this.type = "category";
        },
        getEventColor(event) {
            return '#9e9e9e'; //event.color;
        },
        setToday() {
            this.focus = this.today;
        },
        prev() {
            this.$refs.calendar.prev();

            let todayDate = moment();
            let focusDate = moment(this.focus, 'YYYY-MM-DD');

            this.prevBtnDisabled = true;
            if (focusDate.isAfter(todayDate)) {
                this.prevBtnDisabled = false;
            }
        },
        next() {
            this.$refs.calendar.next();

            let todayDate = moment();
            let focusDate = moment(this.focus, 'YYYY-MM-DD');

            this.prevBtnDisabled = true;
            if (focusDate.isAfter(todayDate)) {
                this.prevBtnDisabled = false;
            }
        },
        checkRateReview(rateReview) {
            if (_.isNumber(rateReview)) {
                this.bookable = true;
                return true;
            }

            this.bookable = false;
            return false;
        },
        getRateReview(rateReview) {
            if (_.isNumber(rateReview)) {
                this.bookable = true;
                this.$store.dispatch("booking/updateBookingCart", {
                    start: this.bookingDay + " " + this.timeStartString,
                    end: this.bookingDay + " " + this.timeEndString,
                    type: this.typeSel ? this.typeSel.type_id : "",
                    option: this.optionSel ? this.optionSel.option_id : "",
                    venue: this.venueSel ? this.venueSel.venue_id : "",
                    sport: this.sportSel ? this.sportSel.sport_id : "",
                    court: this.courtSel ? this.courtSel.court_id : "",
                    parent: this.courtSel ? this.courtSel.parent_id : "",
                }).then(() => {
                    this.$store.dispatch("booking/updateBookingCartRate", {rate_review: this.rateReview});
                });

                return this.$options.filters.currency(rateReview);
            }

            this.bookable = false;
            return rateReview;
        },
        showEvent({ nativeEvent, event }) {
            const open = () => {
                this.selectedEvent = event;
                this.selectedElement = nativeEvent.target;
                setTimeout(() => {
                    this.selectedOpen = true;
                }, 10);
            };

            if (this.selectedOpen) {
                this.selectedOpen = false;
                setTimeout(open, 10);
            } else {
                open();
            }

            nativeEvent.stopPropagation();
        },
        updateRange({ start, end }) {
            this.start = start;
            this.end = end;

            if (this.venueSel) {
                this.getEvents(start.date, this.venueSel.venue_id);
            }
            this.checkAvailDay();
        },
        nth(d) {
            return d > 3 && d < 21 ? "th" : ["th", "st", "nd", "rd", "th", "th", "th", "th", "th", "th"][d % 10];
        },
    },
};
</script>

<style scoped lang="scss">
.v-event-draggable {
    padding: 2px 8px;
    overflow: hidden;
    flex-wrap: wrap;
}

::v-deep .v-calendar .v-event,
::v-deep .v-calendar .v-event-more {
    // width: 100% !important;
    margin: 0 auto !important;
}

::v-deep .theme--light.v-calendar-events .v-event-more {
    background-color: #f4ab42;
}

::v-deep .v-calendar-weekly__day-label {
    margin: 4px 0 4px;
}

::v-deep .v-calendar .v-event-timed-container {
    margin-right: 0px !important;
}

::v-deep .v-calendar .v-event-timed.full-court {
    width: 100% !important;
}
::v-deep .v-calendar .v-event-timed.full-space {
    width: 100% !important;
}
::v-deep .v-calendar .v-event-timed.half-court {
    width: 50% !important;
}
::v-deep .v-calendar .v-event-timed.third-court {
    width: 33.33% !important;
}
::v-deep .v-calendar .v-event-timed.twothird-court {
    width: 66.66% !important;
}

::v-deep .booking-dialog {
    margin-top: 180px !important;
    max-height: 80% !important;
}

::v-deep .vue__time-picker.time-picker {
    width: 90%;
}
::v-deep .vue__time-picker input.display-time {
    width: 100%;
    height: 32px;
    line-height: 20px;
    font-family: 'Oswald', sans-serif;
    font-size: 16px;
    font-weight: 400;
    padding: 8px 4px !important;
    outline: 0 !important;
    border: 1px solid #d2d2d2 !important;
    border-radius: 5px !important;
}

::v-deep .v-text-field.focus-date-text input {
    font-size: 16px !important;
    font-family: 'Oswald',sans-serif !important;
}

.noBook {
    background: #bdbdbd;
    // padding-top: 3px;
    // padding-bottom: 3px;
    height: 100%;
}

@media only screen and (max-width: 767px) {
    ::v-deep .booking-dialog {
        margin-top: 160px !important;
        max-height: 80% !important;
    }
    ::v-deep .v-overlay.overlay-landscape {
        margin-top: 56px;
    }
    ::v-deep .vsheet-header header {
        height: 128px !important;
    }
    ::v-deep .vsheet-header header div {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
    }
}
</style>
